/**
 * AiBackendError — typed value object that represents "what the BE said went wrong".
 *
 * The aumico-ms-ai-agents backend always returns errors with this body shape:
 *   { statusCode, messageKey, messageParams? }
 *
 * On the FE we never store a flat string + key + params triple — instead we
 * wrap it in this class. Components consume one value (`error: AiBackendError | null`)
 * and render via `{{ error.messageKey | i18next: (error.params || {}) }}`.
 *
 * Use `AiBackendError.from(err, fallback)` in NgRx effects' `catchError` blocks
 * to convert any thrown value (HttpErrorResponse, plain Error, anything) into
 * the typed shape — including a safe fallback for non-structured errors.
 */

import { HttpErrorResponse } from '@angular/common/http';

/** Mirrors the BE `AiErrorBody` interface from aumico-ms-ai-agents. */
export interface AiBackendErrorBody {
    statusCode: number;
    messageKey: string;
    messageParams?: Record<string, unknown>;
}

export class AiBackendError {
    private constructor(
        /** i18n key used to translate the error via the i18next pipe. */
        public readonly messageKey: string,
        /** Plain-text fallback shown if the key has no translation entry. */
        public readonly fallbackText: string,
        /** Interpolation params for `messageKey` (e.g. `{ limit: 50000 }`). */
        public readonly params?: Record<string, unknown>,
        /** Original HTTP status code, when known. */
        public readonly statusCode?: number,
    ) {}

    /**
     * Build an AiBackendError from anything caught in `catchError`:
     * - structured BE response → uses the typed messageKey/messageParams
     * - any other Error / unknown → falls back to `aumico_ai_error_unknown`
     */
    public static from(err: unknown, fallbackText: string): AiBackendError {
        if (err instanceof HttpErrorResponse && AiBackendError.isBackendBody(err.error)) {
            return new AiBackendError(
                err.error.messageKey,
                err.error.messageKey,
                err.error.messageParams,
                err.error.statusCode,
            );
        }
        const text = (err as { message?: string })?.message ?? fallbackText;
        return new AiBackendError('aumico_ai_error_unknown', text);
    }

    /**
     * Build an AiBackendError from a job-completed FAILED websocket event,
     * which carries the same fields but spelled with `errorMessage*` prefixes.
     */
    public static fromCompletedEvent(event: {
        errorMessage?: string;
        errorMessageKey?: string;
        errorMessageParams?: Record<string, unknown>;
    }): AiBackendError {
        return new AiBackendError(
            event.errorMessageKey ?? 'aumico_ai_error_unknown',
            event.errorMessage ?? '',
            event.errorMessageParams,
        );
    }

    private static isBackendBody(x: unknown): x is AiBackendErrorBody {
        return typeof x === 'object'
            && x !== null
            && typeof (x as Record<string, unknown>)['messageKey'] === 'string';
    }
}
